import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { invariantResponse } from '@epic-web/invariant'
import {
	type ActionFunctionArgs,
	json,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { Form, useActionData, useLoaderData } from '@remix-run/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Button } from '#app/components/ui/button.js'
import { Icon } from '#app/components/ui/icon.js'
import { ProductsList } from '#app/components/ui/products-list-cards.tsx'
import { getUserId, requireUserId } from '#app/utils/auth.server.js'
import { SITE_FULL_TITLE } from '#app/utils/client-constants.js'
import { prisma } from '#app/utils/db.server.js'
import { redirectWithToast } from '#app/utils/toast.server.js'
import { useOptionalUser } from '#app/utils/user.js'

const MakeOwnedProductsPrivateSchema = z.object({
	isPrivate: z.string(),
})

export async function loader({ params, request }: LoaderFunctionArgs) {
	const loggedInUserId = await getUserId(request)
	const user = await prisma.user.findFirst({
		where: {
			username: params.username,
		},
		select: {
			id: true,
			name: true,
			ownedProducts: {
				orderBy: {
					createdAt: 'desc',
				},
				select: {
					product: {
						select: {
							id: true,
							name: true,
							slug: true,
							images: {
								select: {
									id: true,
									altText: true,
								},
							},
							category: {
								select: {
									slug: true,
								},
							},
						},
					},
					isPrivate: true,
				},
			},
		},
	})

	invariantResponse(user, 'User not found', { status: 404 })

	const isOwnedProductsListPrivate = user.ownedProducts.some(wp => wp.isPrivate)
	const ownedProducts =
		isOwnedProductsListPrivate && user.id !== loggedInUserId
			? []
			: user.ownedProducts.map(wp => wp.product)

	return json({
		user: { id: user.id, name: user.name },
		ownedProducts,
		isOwnedProductsListPrivate,
	})
}

export async function action({ request }: ActionFunctionArgs) {
	const userId = await requireUserId(request)
	const formData = await request.formData()
	const submission = parseWithZod(formData, {
		schema: MakeOwnedProductsPrivateSchema,
	})

	if (submission.status !== 'success') {
		return json(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { isPrivate } = submission.value
	const isPrivateBoolean = isPrivate === 'true'

	await prisma.ownedProduct.updateMany({
		where: { userId },
		data: { isPrivate: isPrivateBoolean },
	})

	return redirectWithToast('', {
		type: 'info',
		title: `Owned products list is now ${isPrivateBoolean ? 'private 🔒' : 'public 🌍'}`,
	})
}

export default function OwnedProducts() {
	const data = useLoaderData<typeof loader>()
	const loggedInUser = useOptionalUser()
	const isLoggedInUser = data.user.id === loggedInUser?.id
	const actionData = useActionData<typeof action>()

	const [form, fields] = useForm({
		id: 'make-owned-products-private',
		constraint: getZodConstraint(MakeOwnedProductsPrivateSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: MakeOwnedProductsPrivateSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	const isPrivate =
		fields.isPrivate.value === 'true' || data.isOwnedProductsListPrivate
	const ownedProductsCount = data.ownedProducts.length

	return (
		<div className="flex flex-col gap-8">
			<div className="flex w-full items-center justify-between">
				{data.isOwnedProductsListPrivate && !isLoggedInUser ? (
					<h6 className="text-h6">
						{data.user.name}'s products owned list is private
					</h6>
				) : (
					<div className="flex w-full items-center justify-between">
						<h5 className="text-lg text-foreground">
							<span className="font-bold">{ownedProductsCount}</span>{' '}
							<span>
								{ownedProductsCount === 1 ? 'product' : 'products'} owned
							</span>
						</h5>
						{isLoggedInUser && (
							<Form method="POST" {...getFormProps(form)}>
								<input
									{...getInputProps(fields.isPrivate, { type: 'hidden' })}
									value={(!isPrivate).toString()}
								/>
								<Button
									type="submit"
									variant="secondary"
									aria-label={
										isPrivate
											? 'Make products owned public'
											: 'Make products owned private'
									}
								>
									<Icon name={isPrivate ? 'lock-closed' : 'lock-open-1'} />
								</Button>
							</Form>
						)}
					</div>
				)}
			</div>
			<ProductsList
				products={data.ownedProducts.filter(product => product !== null)}
			/>
		</div>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const displayName = data?.user.name ?? params.username
	const contentTitle = 'Products Owned'

	return [
		{ title: `${displayName}'s ${contentTitle} | ${SITE_FULL_TITLE}` },
		{
			name: 'description',
			content: `See ${displayName}'s ${contentTitle} on ${SITE_FULL_TITLE}`,
		},
	]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
